import type {
  AddManualWorkSessionRequest,
  AddTicketMessageRequest,
  AuthResponse,
  ChangePasswordRequest,
  ClientOptionRecord,
  ClientRecord,
  ClientSourceOption,
  ClientsPageResponse,
  ClientStats,
  CreateClientRequest,
  CreateDepartmentRequest,
  CreateEmployeeRequest,
  CreateIntegrationRequest,
  CreateProjectRequest,
  CreateNoteRequest,
  CreateReminderRequest,
  CreateRecurringSeriesRequest,
  CreateTicketRequest,
  CreateLeaveRequestRequest,
  CreateTaskChecklistItemRequest,
  CreateTaskCommentRequest,
  CreateTaskRequest,
  DashboardResponse,
  DocumentRecord,
  ForgotPasswordRequest,
  IntegrationRecord,
  LeaveRequestRecord,
  LoginRequest,
  NoteAttachmentRecord,
  NoteDetailRecord,
  NoteSummary,
  NotificationListResponse,
  ProjectBudgetResponse,
  ReminderRecord,
  ReportGranularity,
  ResetPasswordRequest,
  SessionUser,
  TaskBillingResponse,
  TaskChecklistItemRecord,
  TaskCommentRecord,
  TaskDetailRecord,
  TaskSummary,
  TestIntegrationResponse,
  UpdateIntegrationRequest,
  UpdateNoteAccessRequest,
  UpdateNoteRequest,
  UpdateReminderRequest,
  UploadAvatarRequest,
  UploadDocumentRequest,
  UploadNoteAttachmentRequest,
  ReorderTaskRequest,
  TicketDetailResponse,
  TimeReportResponse,
  UpdateClientRequest,
  UpdateMyProfileRequest,
  UpdateProjectRequest,
  UpdateRecurringSeriesRequest,
  UpdateTaskChecklistItemRequest,
  UpdateTaskRequest,
  UpdateClientContactsRequest,
  UpdateEmployeeRequest,
  UpdateWorkSessionRequest,
  UpsertClientProfileRequest,
  ViewPreferenceRecord,
  SaveViewPreferenceRequest,
  WorkSessionActionResponse
} from "@platform/types";

const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL || "/api").replace(/\/+$/, "");

function makeUrl(path: string) {
  return `${apiBaseUrl}${path.startsWith("/") ? path : `/${path}`}`;
}

export function resolveAssetUrl(path: string) {
  return makeUrl(path);
}

function normalizeErrorMessage(status: number, text: string) {
  const trimmed = text.trim();

  if (!trimmed) {
    return `Request failed with status ${status}`;
  }

  if (trimmed.startsWith("<!DOCTYPE html") || trimmed.startsWith("<html")) {
    return `API endpoint returned HTML instead of JSON (${status}). Sprawdź konfiguracje backendu lub VITE_API_BASE_URL.`;
  }

  try {
    const parsed = JSON.parse(trimmed) as {
      code?: string;
      error?: string;
      message?: string;
      statusCode?: number;
    };

    if (parsed.message) {
      if (parsed.message.includes("Route ") && parsed.message.includes(" not found")) {
        return "Ta funkcja nie jest jeszcze dostępna na aktualnie uruchomionej wersji API. Zrestartuj backend po wdrożeniu najnowszych zmian.";
      }

      if (parsed.message === "Invalid token" || parsed.message === "Session expired") {
        return "Sesja wygasła lub jest nieprawidłowa. Zaloguj się ponownie.";
      }

      return parsed.message;
    }
  } catch {
    // Fallback to raw text below when response is not JSON.
  }

  return trimmed;
}

export class HttpError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "HttpError";
    this.status = status;
  }
}

async function requestJson<T>(path: string, init?: RequestInit): Promise<T> {
  const token = window.localStorage.getItem("auth_token");
  const hasJsonBody = typeof init?.body === "string";
  let response: Response;
  try {
    response = await fetch(makeUrl(path), {
      headers: {
        ...(hasJsonBody ? { "content-type": "application/json" } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(init?.headers ?? {})
      },
      ...init
    });
  } catch {
    // Network failure (offline, DNS, connection reset) - not an auth problem, so callers
    // must not treat this the same as a 401 (e.g. by logging the user out).
    throw new HttpError(0, "Nie udało się połączyć z serwerem. Sprawdź połączenie internetowe.");
  }

  if (!response.ok) {
    const text = await response.text();
    throw new HttpError(response.status, normalizeErrorMessage(response.status, text));
  }

  return response.json() as Promise<T>;
}

export function login(payload: LoginRequest) {
  return requestJson<AuthResponse>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function fetchDashboard() {
  return requestJson<DashboardResponse>("/api/dashboard");
}

export function createTask(payload: CreateTaskRequest) {
  return requestJson<{ task: DashboardResponse["employee"]["tasks"][number] }>("/api/tasks", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export async function fetchTaskArchive(employeeId?: string) {
  const suffix = employeeId ? `?employeeId=${encodeURIComponent(employeeId)}` : "";
  const response = await requestJson<{ items: TaskSummary[] }>(`/api/tasks/archive${suffix}`);
  return response.items;
}

export function createClient(payload: CreateClientRequest) {
  return requestJson<{ client: ClientRecord }>("/api/clients", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function createProject(payload: CreateProjectRequest) {
  return requestJson<{ project: DashboardResponse["admin"]["projects"][number] }>("/api/projects", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function updateProject(projectId: string, payload: UpdateProjectRequest) {
  return requestJson<{ project: DashboardResponse["admin"]["projects"][number] }>(`/api/projects/${projectId}`, {
    method: "PUT",
    body: JSON.stringify(payload)
  });
}

export function deleteProject(projectId: string) {
  return requestJson<{ ok: boolean }>(`/api/projects/${projectId}`, {
    method: "DELETE"
  });
}

export function startTaskSession(taskId: string) {
  return requestJson<WorkSessionActionResponse>(`/api/tasks/${taskId}/start`, {
    method: "POST"
  });
}

export function stopTaskSession(sessionId: string) {
  return requestJson<WorkSessionActionResponse>(`/api/work-sessions/${sessionId}/stop`, {
    method: "POST"
  });
}

export function addManualWorkSession(taskId: string, payload: AddManualWorkSessionRequest) {
  return requestJson<{ task: TaskDetailRecord }>(`/api/tasks/${taskId}/work-sessions`, {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function deleteWorkSession(taskId: string, sessionId: string) {
  return requestJson<{ task: TaskDetailRecord }>(`/api/tasks/${taskId}/work-sessions/${sessionId}`, {
    method: "DELETE"
  });
}

export function updateWorkSession(taskId: string, sessionId: string, payload: UpdateWorkSessionRequest) {
  return requestJson<{ task: TaskDetailRecord }>(`/api/tasks/${taskId}/work-sessions/${sessionId}`, {
    method: "PATCH",
    body: JSON.stringify(payload)
  });
}

export function createRecurringTaskSeries(payload: CreateRecurringSeriesRequest) {
  return requestJson<{ series: unknown }>("/api/recurring-tasks", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function updateRecurringSeries(seriesId: string, payload: UpdateRecurringSeriesRequest) {
  return requestJson<{ ok: boolean }>(`/api/recurring-tasks/${seriesId}`, {
    method: "PATCH",
    body: JSON.stringify(payload)
  });
}

export function deleteRecurringSeries(seriesId: string) {
  return requestJson<{ ok: boolean }>(`/api/recurring-tasks/${seriesId}`, {
    method: "DELETE"
  });
}

export function fetchTicketDetail(ticketId: string) {
  return requestJson<TicketDetailResponse>(`/api/tickets/${ticketId}`);
}

export function createTicket(payload: CreateTicketRequest) {
  return requestJson<TicketDetailResponse>("/api/tickets", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function addTicketMessage(ticketId: string, payload: AddTicketMessageRequest) {
  return requestJson<TicketDetailResponse>(`/api/tickets/${ticketId}/messages`, {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function closeTicket(ticketId: string) {
  return requestJson<TicketDetailResponse>(`/api/tickets/${ticketId}/close`, {
    method: "POST"
  });
}

export function reopenTicket(ticketId: string) {
  return requestJson<TicketDetailResponse>(`/api/tickets/${ticketId}/reopen`, {
    method: "POST"
  });
}

export function updateClientProfile(payload: UpsertClientProfileRequest) {
  return requestJson<{ profile: DashboardResponse["client"]["profile"] }>("/api/client-profile", {
    method: "PUT",
    body: JSON.stringify(payload)
  });
}

export function updateClient(clientId: string, payload: UpdateClientRequest) {
  return requestJson<{ client: ClientRecord }>(`/api/clients/${clientId}`, {
    method: "PUT",
    body: JSON.stringify(payload)
  });
}

export function deleteClient(clientId: string) {
  return requestJson<{ ok: boolean }>(`/api/clients/${clientId}`, {
    method: "DELETE"
  });
}

export function updateClientContacts(payload: UpdateClientContactsRequest) {
  return requestJson<{ profile: DashboardResponse["client"]["profile"] }>("/api/client-profile/contacts", {
    method: "PUT",
    body: JSON.stringify(payload)
  });
}

export function createEmployee(payload: CreateEmployeeRequest) {
  return requestJson<{ employee: DashboardResponse["admin"]["employees"][number] }>("/api/employees", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function createDepartment(payload: CreateDepartmentRequest) {
  return requestJson<{ department: DashboardResponse["admin"]["departments"][number] }>("/api/departments", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function updateEmployee(employeeId: string, payload: UpdateEmployeeRequest) {
  return requestJson<{ employee: DashboardResponse["admin"]["employees"][number] }>(`/api/employees/${employeeId}`, {
    method: "PUT",
    body: JSON.stringify(payload)
  });
}

export function deleteEmployee(employeeId: string) {
  return requestJson<{ ok: boolean }>(`/api/employees/${employeeId}`, {
    method: "DELETE"
  });
}

export function sendEmployeePasswordResetLink(employeeId: string) {
  return requestJson<{ ok: boolean }>(`/api/employees/${employeeId}/password-reset-link`, {
    method: "POST"
  });
}

export function setEmployeePassword(employeeId: string, newPassword: string) {
  return requestJson<{ ok: boolean }>(`/api/employees/${employeeId}/set-password`, {
    method: "POST",
    body: JSON.stringify({ newPassword })
  });
}

export function fetchTaskComments(taskId: string) {
  return requestJson<{ items: TaskCommentRecord[] }>(`/api/tasks/${taskId}/comments`);
}

export function fetchTaskDetail(taskId: string) {
  return requestJson<{ task: TaskDetailRecord }>(`/api/tasks/${taskId}`);
}

export function createTaskComment(taskId: string, payload: CreateTaskCommentRequest) {
  return requestJson<{ items: TaskCommentRecord[] }>(`/api/tasks/${taskId}/comments`, {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function updateTaskComment(taskId: string, commentId: string, payload: { body: string }) {
  return requestJson<{ items: TaskCommentRecord[] }>(`/api/tasks/${taskId}/comments/${commentId}`, {
    method: "PATCH",
    body: JSON.stringify(payload)
  });
}

export function deleteTaskComment(taskId: string, commentId: string) {
  return requestJson<{ items: TaskCommentRecord[] }>(`/api/tasks/${taskId}/comments/${commentId}`, {
    method: "DELETE"
  });
}

export function addTaskChecklistItem(taskId: string, payload: CreateTaskChecklistItemRequest) {
  return requestJson<{ items: TaskChecklistItemRecord[] }>(`/api/tasks/${taskId}/checklist`, {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function updateTaskChecklistItem(taskId: string, itemId: string, payload: UpdateTaskChecklistItemRequest) {
  return requestJson<{ items: TaskChecklistItemRecord[] }>(`/api/tasks/${taskId}/checklist/${itemId}`, {
    method: "PATCH",
    body: JSON.stringify(payload)
  });
}

export function deleteTaskChecklistItem(taskId: string, itemId: string) {
  return requestJson<{ items: TaskChecklistItemRecord[] }>(`/api/tasks/${taskId}/checklist/${itemId}`, {
    method: "DELETE"
  });
}

export function updateTask(taskId: string, payload: UpdateTaskRequest) {
  return requestJson<{ task: TaskDetailRecord | DashboardResponse["employee"]["tasks"][number] }>(`/api/tasks/${taskId}`, {
    method: "PATCH",
    body: JSON.stringify(payload)
  });
}

export function reorderTask(taskId: string, payload: ReorderTaskRequest) {
  return requestJson<{ task: TaskDetailRecord | DashboardResponse["employee"]["tasks"][number] }>(`/api/tasks/${taskId}/reorder`, {
    method: "PATCH",
    body: JSON.stringify(payload)
  });
}

export function deleteTask(taskId: string) {
  return requestJson<{ ok: boolean }>(`/api/tasks/${taskId}`, {
    method: "DELETE"
  });
}

export function changePassword(payload: ChangePasswordRequest) {
  return requestJson<{ ok: boolean; token?: string }>("/api/auth/change-password", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function updateMyProfile(payload: UpdateMyProfileRequest) {
  return requestJson<{ user: SessionUser }>("/api/auth/me", {
    method: "PATCH",
    body: JSON.stringify(payload)
  });
}

export function uploadAvatar(payload: UploadAvatarRequest) {
  return requestJson<{ user: SessionUser }>("/api/me/avatar", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function deleteAvatar() {
  return requestJson<{ user: SessionUser }>("/api/me/avatar", {
    method: "DELETE"
  });
}

export function requestPasswordReset(payload: ForgotPasswordRequest) {
  return requestJson<{ ok: boolean }>("/api/auth/forgot-password", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function resetPassword(payload: ResetPasswordRequest) {
  return requestJson<{ ok: boolean }>("/api/auth/reset-password", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function fetchTimeReport(
  granularity: ReportGranularity,
  range?: { from: string; to: string },
  mode: "period" | "taskTotal" = "period",
  mine?: boolean,
  clientId?: string
) {
  const params = new URLSearchParams({ granularity, mode });
  if (range) {
    params.set("from", range.from);
    params.set("to", range.to);
  }
  if (mine) {
    params.set("mine", "1");
  }
  if (clientId) {
    params.set("clientId", clientId);
  }
  return requestJson<TimeReportResponse>(`/api/reports/time?${params.toString()}`);
}

export function fetchTaskBilling() {
  return requestJson<TaskBillingResponse>("/api/billing/tasks");
}

export function setTaskPaid(taskId: string, paid: boolean) {
  return requestJson<{ ok: true }>(`/api/tasks/${taskId}/paid`, {
    method: "POST",
    body: JSON.stringify({ paid })
  });
}

export function fetchProjectBudget(projectId: string) {
  return requestJson<ProjectBudgetResponse>(`/api/projects/${projectId}/budget`);
}

export function fetchDocuments(clientId?: string, projectId?: string) {
  const params = new URLSearchParams();
  if (clientId) params.set("clientId", clientId);
  if (projectId) params.set("projectId", projectId);
  const query = params.toString();
  return requestJson<{ items: DocumentRecord[] }>(`/api/documents${query ? `?${query}` : ""}`);
}

export function uploadDocument(payload: UploadDocumentRequest) {
  return requestJson<{ document: DocumentRecord }>("/api/documents", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function deleteDocument(documentId: string) {
  return requestJson<{ ok: true }>(`/api/documents/${documentId}`, {
    method: "DELETE"
  });
}

export async function downloadDocument(documentId: string, fileName: string) {
  const token = window.localStorage.getItem("auth_token");
  const response = await fetch(makeUrl(`/api/documents/${documentId}/download`), {
    headers: token ? { authorization: `Bearer ${token}` } : {}
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(normalizeErrorMessage(response.status, text));
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

async function fetchProtectedBlob(path: string) {
  const token = window.localStorage.getItem("auth_token");
  const response = await fetch(makeUrl(path), {
    headers: token ? { authorization: `Bearer ${token}` } : {}
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(normalizeErrorMessage(response.status, text));
  }

  return response.blob();
}

export function fetchTicketAttachmentBlob(attachmentId: string) {
  return fetchProtectedBlob(`/api/tickets/attachments/${attachmentId}/download`);
}

export async function downloadTicketAttachment(attachmentId: string, fileName: string) {
  const blob = await fetchTicketAttachmentBlob(attachmentId);
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

export function fetchLeaveRequests() {
  return requestJson<{ items: LeaveRequestRecord[] }>("/api/leave-requests");
}

export function createLeaveRequest(payload: CreateLeaveRequestRequest) {
  return requestJson<{ leaveRequest: LeaveRequestRecord }>("/api/leave-requests", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function decideLeaveRequest(leaveRequestId: string, status: "APPROVED" | "REJECTED") {
  return requestJson<{ leaveRequest: LeaveRequestRecord }>(`/api/leave-requests/${leaveRequestId}/decide`, {
    method: "POST",
    body: JSON.stringify({ status })
  });
}

export function fetchViewPreference(viewKey: string) {
  return requestJson<{ preference: ViewPreferenceRecord | null }>(`/api/view-preferences/${encodeURIComponent(viewKey)}`);
}

export function saveViewPreference(viewKey: string, payload: SaveViewPreferenceRequest) {
  return requestJson<{ preference: ViewPreferenceRecord }>(`/api/view-preferences/${encodeURIComponent(viewKey)}`, {
    method: "PUT",
    body: JSON.stringify(payload)
  });
}

const COLLAPSED_SUBTASKS_VIEW_KEY = "task-subtasks-collapsed";

export async function fetchCollapsedSubtaskIds(): Promise<string[]> {
  const { preference } = await requestJson<{ preference: { columns: unknown } | null }>(
    `/api/view-preferences/${COLLAPSED_SUBTASKS_VIEW_KEY}`
  );
  const stored = preference?.columns;
  return Array.isArray(stored) ? stored.filter((id): id is string => typeof id === "string") : [];
}

export function saveCollapsedSubtaskIds(collapsedTaskIds: string[]) {
  return requestJson<{ preference: unknown }>(`/api/view-preferences/${COLLAPSED_SUBTASKS_VIEW_KEY}`, {
    method: "PUT",
    body: JSON.stringify({ columns: collapsedTaskIds })
  });
}

export function fetchNotes() {
  return requestJson<{ items: NoteSummary[] }>("/api/notes");
}

export function fetchNoteDetail(noteId: string) {
  return requestJson<{ note: NoteDetailRecord }>(`/api/notes/${noteId}`);
}

export function createNote(payload: CreateNoteRequest) {
  return requestJson<{ note: NoteDetailRecord }>("/api/notes", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function updateNote(noteId: string, payload: UpdateNoteRequest) {
  return requestJson<{ note: NoteDetailRecord }>(`/api/notes/${noteId}`, {
    method: "PATCH",
    body: JSON.stringify(payload)
  });
}

export function deleteNote(noteId: string) {
  return requestJson<{ ok: boolean }>(`/api/notes/${noteId}`, {
    method: "DELETE"
  });
}

export function updateNoteAccess(noteId: string, payload: UpdateNoteAccessRequest) {
  return requestJson<{ note: NoteDetailRecord }>(`/api/notes/${noteId}/access`, {
    method: "PUT",
    body: JSON.stringify(payload)
  });
}

export function uploadNoteAttachment(noteId: string, payload: UploadNoteAttachmentRequest) {
  return requestJson<{ attachment: NoteAttachmentRecord }>(`/api/notes/${noteId}/attachments`, {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function fetchNoteAttachmentBlob(attachmentUrl: string) {
  return fetchProtectedBlob(attachmentUrl);
}

export function deleteNoteAttachment(noteId: string, attachmentId: string) {
  return requestJson<{ ok: true }>(`/api/notes/${noteId}/attachments/${attachmentId}`, {
    method: "DELETE"
  });
}

export function fetchTaskReminders(taskId: string) {
  return requestJson<{ items: ReminderRecord[] }>(`/api/tasks/${taskId}/reminders`);
}

export function fetchReminders() {
  return requestJson<{ items: ReminderRecord[] }>("/api/reminders");
}

export function fetchDueReminders() {
  return requestJson<{ items: ReminderRecord[] }>("/api/reminders/due");
}

export function fetchNotifications(page = 1, pageSize = 20) {
  return requestJson<NotificationListResponse>(`/api/notifications?page=${page}&pageSize=${pageSize}`);
}

export function markNotificationRead(notificationId: string) {
  return requestJson<{ ok: true }>(`/api/notifications/${notificationId}/read`, { method: "POST" });
}

export function markAllNotificationsRead() {
  return requestJson<{ ok: true }>("/api/notifications/read-all", { method: "POST" });
}

export function ensureAppUpdateNotification(payload: { version: string; title: string; body: string }) {
  return requestJson<{ id: string }>("/api/notifications/app-update", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function createReminder(payload: CreateReminderRequest) {
  return requestJson<{ reminder: ReminderRecord }>("/api/reminders", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function updateReminder(reminderId: string, payload: UpdateReminderRequest) {
  return requestJson<{ reminder: ReminderRecord }>(`/api/reminders/${reminderId}`, {
    method: "PATCH",
    body: JSON.stringify(payload)
  });
}

export function deleteReminder(reminderId: string) {
  return requestJson<{ ok: boolean }>(`/api/reminders/${reminderId}`, {
    method: "DELETE"
  });
}

export function fetchNotesForClient(clientId: string) {
  return requestJson<{ items: NoteSummary[] }>(`/api/clients/${clientId}/notes`);
}

export function fetchRemindersForClient(clientId: string) {
  return requestJson<{ items: ReminderRecord[] }>(`/api/clients/${clientId}/reminders`);
}

export function fetchClientSources() {
  return requestJson<{ items: ClientSourceOption[] }>("/api/client-sources");
}

export async function fetchClientOptions(params?: { search?: string; id?: string }) {
  const query = new URLSearchParams();
  if (params?.search) query.set("search", params.search);
  if (params?.id) query.set("id", params.id);
  const suffix = query.toString() ? `?${query.toString()}` : "";
  const response = await requestJson<{ items: ClientOptionRecord[] }>(`/api/clients/options${suffix}`);
  return response.items;
}

export function fetchClientsPage(params?: { page?: number; pageSize?: number; search?: string; sourceKey?: string }) {
  const query = new URLSearchParams();
  if (params?.page) query.set("page", String(params.page));
  if (params?.pageSize) query.set("pageSize", String(params.pageSize));
  if (params?.search) query.set("search", params.search);
  if (params?.sourceKey) query.set("sourceKey", params.sourceKey);
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return requestJson<ClientsPageResponse>(`/api/clients${suffix}`);
}

export async function fetchClientById(clientId: string) {
  const response = await requestJson<{ client: ClientRecord }>(`/api/clients/${clientId}`);
  return response.client;
}

export function fetchClientStats() {
  return requestJson<ClientStats>("/api/clients/stats");
}

export function fetchIntegrations() {
  return requestJson<{ items: IntegrationRecord[] }>("/api/admin/integrations");
}

export function createIntegration(payload: CreateIntegrationRequest) {
  return requestJson<{ integration: IntegrationRecord }>("/api/admin/integrations", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function updateIntegration(integrationId: string, payload: UpdateIntegrationRequest) {
  return requestJson<{ integration: IntegrationRecord }>(`/api/admin/integrations/${integrationId}`, {
    method: "PATCH",
    body: JSON.stringify(payload)
  });
}

export function deleteIntegration(integrationId: string) {
  return requestJson<{ ok: boolean }>(`/api/admin/integrations/${integrationId}`, {
    method: "DELETE"
  });
}

export function testIntegrationConnection(payload: { type: CreateIntegrationRequest["type"]; integrationId?: string; config: CreateIntegrationRequest["config"] }) {
  return requestJson<TestIntegrationResponse>("/api/admin/integrations/test", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function syncIntegration(integrationId: string) {
  return requestJson<{ ok: boolean }>(`/api/admin/integrations/${integrationId}/sync`, {
    method: "POST"
  });
}

const CLIENT_SOURCES_VIEW_KEY = "client-sources-visible";

export async function fetchVisibleClientSourceKeys(): Promise<string[] | null> {
  const { preference } = await requestJson<{ preference: { columns: unknown } | null }>(
    `/api/view-preferences/${CLIENT_SOURCES_VIEW_KEY}`
  );
  const stored = preference?.columns;
  return Array.isArray(stored) ? stored.filter((key): key is string => typeof key === "string") : null;
}

export function saveVisibleClientSourceKeys(sourceKeys: string[]) {
  return requestJson<{ preference: unknown }>(`/api/view-preferences/${CLIENT_SOURCES_VIEW_KEY}`, {
    method: "PUT",
    body: JSON.stringify({ columns: sourceKeys })
  });
}
